<template>
  <div>
    <template v-if="readOnly">
      <div v-if="viewItem() !== null && viewItem() !== undefined"
           class="p-2 italic"
           :class="{
             'text-right w-full': valueIsNumber && singleLine,
             'overflow-hidden text-ellipsis whitespace-nowrap not-italic': singleLine,
           }">
        {{ viewItem() }}
      </div>
      <div v-else class="p-2 italic text-fg-muted">
        (blank)
      </div>
    </template>
    <template v-else>
      <BaseAutoComplete
        v-if="hasAllowedValues"
        :id="id"
        :model-value="modelValue"
        :allowed-values="allowedValues"
        :is-clearable="header.isNullable"
        :error="fieldError"
        @update:model-value="changed"
      />
      <BaseInput
        v-else
        :id="id"
        :model-value="modelValue"
        :error="fieldError"
        class="w-full"
        @change="changed"
      />
      <BaseProgressBar v-if="saving" />
      <div v-else class="h-1 m-0 p-0" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useTableStore } from '@/store/storeTable'
import { useValidationRulesStore } from '@/store/validationRulesStore'
import { formatDate } from '@/helpers/DateFormatter'
import { useUserPrefFormatting } from '@/composables/useUserPrefFormatting'
import BaseInput from '@/components/BaseComponents/BaseInput.vue'
import BaseProgressBar from '@/components/BaseComponents/BaseProgressBar.vue'
import BaseAutoComplete from '@/components/BaseComponents/BaseAutoComplete.vue'
import { useCellSave } from '@/composables/useCellSave'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Array, Date, Object],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
  onSaveSuccess: { type: Function, default: null },
  // US 3399: valgfri id som settes paa selve kontrollen, saa en etikett kan knyttes med for=.
  // Uten den erklaeringen ville Vues attribute-fallthrough lagt id-en paa rot-diven, og en
  // for=-peker dit navngir ingenting for en skjermleser selv om statisk analyse blir fornoeyd.
  id: { type: String, default: undefined }
});

const emit = defineEmits(['update:modelValue'])

const { saving, saveErrorMessage, saveCell } = useCellSave()

const tableStore = useTableStore()
const validationRulesStore = useValidationRulesStore()
const { formatNumber, dateFormat } = useUserPrefFormatting()

const columnName = computed(() => props.header.field)

// Hent allowedValues fra Pinia store (ikke async - leser fra cache)
const allowedValues = computed(() => {
  if (!columnName.value) {
    return null
  }
  return validationRulesStore.getAllowedValuesForColumn(columnName.value)
})

const hasAllowedValues = computed(() => {
  return allowedValues.value !== null && allowedValues.value.length > 0
})

const isValidValue = computed(() => {
  if (!hasAllowedValues.value || !props.modelValue) return true
  const currentValue = String(props.modelValue).trim()
  if (!currentValue) return true // Tom verdi er OK hvis nullable
  const normalizedCurrentValue = currentValue.toLowerCase()
  return allowedValues.value!.some((v: string | number) => String(v).trim().toLowerCase() === normalizedCurrentValue)
})

const validationError = computed(() => {
  if (!hasAllowedValues.value || !props.modelValue) return null
  if (!isValidValue.value) {
    return 'Ugyldig verdi'
  }
  return null
})

const fieldError = computed(() => {
  if (validationError.value) return validationError.value
  return saveErrorMessage.value
})


async function changed (newValue: any) {
  emit('update:modelValue', newValue)

  if (!props.autoSave) return;

  const item = {
    tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
    rowId: props.rowId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue
  };
  await saveCell(item, props.onSaveSuccess as (() => void | Promise<void>) | null);
}

const valueIsNumber = ref(false)

function viewItem ()
{
  valueIsNumber.value = false
  if (props.modelValue !== null && props.modelValue !== undefined) {
    const primaryFieldType = String(props.header.primaryFieldType ?? '').toLowerCase().trim()
    const componentEdit = String(props.header.componentEdit ?? '').toLowerCase().trim()

    const isDateOrTimeField = [
      'date',
      'datetime',
      'time'
    ].includes(primaryFieldType) || ['editdate', 'edittime'].includes(componentEdit)

    if (isDateOrTimeField) {
      return formatDate(props.modelValue, undefined, dateFormat.value)
    }

    const isNumericField = [
      'integer',
      'bigint',
      'decimal',
      'percent'
    ].includes(primaryFieldType) || ['editnumber', 'editpercent'].includes(componentEdit)

    if (isNumericField) {
      const num = Number(props.modelValue)
      if (!Number.isFinite(num)) {
        return props.modelValue
      }
      valueIsNumber.value = true
      // WI 2086: tallformatering følger brukerens regionale prefs.
      const useThousands = props.header.numberFormatShowThousandsSeparator !== false
      return formatNumber(num, { useGrouping: useThousands })
    }
  }
  return props.modelValue
}
</script>
